import {
  Car,
  FileText,
  House,
  Airplane,
  Shield,
  Star,
  Umbrella,
  Vault,
} from '@phosphor-icons/react';

import pt from '../locales/pt';
import { paths } from '../routes/paths';

/** @typedef {'dream' | 'duty' | 'safe' | 'other'} PotPurpose */

/**
 * @typedef {{ pathname: string, state?: object } | null} PotOriginNavigation
 */

export const POT_PURPOSE_ORDER = ['dream', 'duty', 'safe', 'other'];

/**
 * @param {object} pot
 * @returns {PotPurpose}
 */
export function resolvePotPurpose(pot) {
  if (pot?.kind === 'occasional') return 'duty';
  if (pot?.is_emergency_fund_goal) return 'safe';
  if (pot?.kind === 'goal') return 'dream';
  return 'other';
}

/**
 * Resolve navigation target for a vault row (mapa → origem).
 * FE goes to emergency fund page, not SMART goal detail.
 * @param {object} pot
 * @returns {PotOriginNavigation}
 */
export function resolvePotOriginPath(pot) {
  if (!pot) return null;

  if (pot.is_emergency_fund_goal) {
    return { pathname: paths.emergencyFund };
  }

  if (pot.kind === 'goal' && pot.goal) {
    return { pathname: paths.goal(pot.goal) };
  }

  if (pot.kind === 'occasional') {
    const month =
      pot.occasional_month ||
      (pot.target_date
        ? new Date(`${pot.target_date}T12:00:00`).getMonth() + 1
        : null);
    const year = pot.target_date
      ? new Date(`${pot.target_date}T12:00:00`).getFullYear()
      : new Date().getFullYear();
    return {
      pathname: paths.calendar,
      state: month ? { month, year } : undefined,
    };
  }

  return null;
}

/**
 * @param {object[]} pots
 * @returns {Record<PotPurpose, object[]>}
 */
export function groupPotsByPurpose(pots) {
  const groups = {
    dream: [],
    duty: [],
    safe: [],
    other: [],
  };
  for (const pot of pots || []) {
    groups[resolvePotPurpose(pot)].push(pot);
  }
  return groups;
}

const PURPOSE_META = {
  dream: {
    labelKey: 'dream',
    icon: Star,
    iconBg: 'bg-rose-100 dark:bg-rose-950/40',
    iconColor: 'text-rose-800 dark:text-rose-300',
    track: 'bg-rose',
    trackClass: 'bg-rose',
  },
  duty: {
    labelKey: 'duty',
    icon: Umbrella,
    iconBg: 'bg-amber-100 dark:bg-amber-950/40',
    iconColor: 'text-amber-800 dark:text-amber-300',
    track: 'bg-amber',
    trackClass: 'bg-amber',
  },
  safe: {
    labelKey: 'safe',
    icon: Shield,
    iconBg: 'bg-sage-100 dark:bg-sage-900/40',
    iconColor: 'text-sage-800 dark:text-sage-300',
    track: 'bg-sage-500',
    trackClass: 'bg-sage-500',
  },
  other: {
    labelKey: 'other',
    icon: Vault,
    iconBg: 'bg-stone-100 dark:bg-stone-800/60',
    iconColor: 'text-stone-600 dark:text-stone-300',
    track: 'bg-stone-400',
    trackClass: 'bg-stone-400',
  },
};

export function getPurposeMeta(purpose) {
  const meta = PURPOSE_META[purpose] || PURPOSE_META.other;
  return {
    ...meta,
    label: pt.pots.purposes[meta.labelKey],
  };
}

const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

function formatMonthYear(date) {
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${year}`;
}

/**
 * Optional EF coverage months if present on pot (API may not send yet).
 * @param {object} pot
 * @returns {number | null}
 */
function resolveEmergencyFundMonths(pot) {
  const raw =
    pot?.ef_months_of_coverage ??
    pot?.target_months ??
    pot?.emergency_fund_months;
  const months = Number(raw);
  if (!Number.isFinite(months) || months <= 0) return null;
  return Math.round(months);
}

/**
 * @param {object} pot
 * @returns {string}
 */
export function formatPotTargetLabel(pot) {
  if (pot?.is_emergency_fund_goal) {
    const months = resolveEmergencyFundMonths(pot);
    if (months != null) {
      return pt.pots.vaultRow.emergencyMetaMonths.replace(
        '{months}',
        String(months),
      );
    }
    return pt.pots.vaultRow.emergencyMeta;
  }
  if (pot?.target_date) {
    const date = new Date(`${pot.target_date}T12:00:00`);
    if (!Number.isNaN(date.getTime())) {
      return pt.pots.vaultRow.forDate.replace('{date}', formatMonthYear(date));
    }
  }
  if (pot?.occasional_month) {
    const monthName = MONTH_NAMES[pot.occasional_month - 1];
    return pt.pots.vaultRow.forMonth.replace('{month}', monthName);
  }
  return pt.pots.vaultRow.noDate;
}

/**
 * Pick a contextual icon for a vault row.
 * @param {object} pot
 * @param {PotPurpose} purpose
 */
export function resolvePotVaultIcon(pot, purpose) {
  if (purpose === 'safe') return Shield;
  if (purpose === 'duty') {
    const name = (pot?.name || '').toLowerCase();
    if (name.includes('iuc') || name.includes('car')) return Car;
    if (name.includes('irs') || name.includes('imi')) return FileText;
    return Umbrella;
  }
  if (purpose === 'dream') {
    const name = (pot?.name || '').toLowerCase();
    if (name.includes('casa') || name.includes('entrada')) return House;
    if (name.includes('viagem') || name.includes('jap')) return Airplane;
    return Star;
  }
  return Vault;
}

/**
 * @param {object[]} pots
 * @returns {number}
 */
export function sumPotAccumulated(pots) {
  return (pots || []).reduce(
    (sum, pot) => sum + Number(pot?.accumulated || 0),
    0,
  );
}
